import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, UsersRound } from 'lucide-react';

import type { CharactersListParams } from '../../api/endpoints';
import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  EmptyState,
  SearchInput,
  Seg,
  Skeleton,
  type SegOption,
} from '../../components/ui';
import { useCharacters, useCurrentUser } from '../../hooks';
import {
  CHARACTER_CATEGORIES,
  CHARACTER_CATEGORY_LABELS,
  type Character,
  type CharacterCategory,
} from '../../types/api';
import { AddCharacterDialog } from './AddCharacterDialog';
import { CharacterCard } from './CharacterCard';
import { CharacterDetail } from './CharacterDetail';
import styles from './Characters.module.css';

type TabKey = 'all' | CharacterCategory;

interface TabDef {
  key: TabKey;
  label: string;
  params: CharactersListParams;
  emptyText: string;
}

const TABS: readonly TabDef[] = [
  {
    key: 'all',
    label: 'Все',
    params: { is_active: true },
    emptyText:
      'Персонажей пока нет.',
  },
  ...CHARACTER_CATEGORIES.map((category) => ({
    key: category as TabKey,
    label: CHARACTER_CATEGORY_LABELS[category],
    params: { is_active: true, category } as CharactersListParams,
    emptyText: `В категории «${CHARACTER_CATEGORY_LABELS[category]}» пока никого.`,
  })),
];

export function CharactersPage() {
  const [params, setParams] = useSearchParams();
  const openId = params.get('id');
  const { can, isLoading: userLoading } = useCurrentUser();
  const canEdit = can('editor');

  const [tab, setTab] = useState<TabKey>('all');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const search = useDebounced(query.trim(), 300);

  const active = TABS.find((item) => item.key === tab) ?? (TABS[0] as TabDef);
  const list = useCharacters({ ...active.params, q: search || undefined, limit: 60 });

  const countAll = useCharacters({ is_active: true, limit: 1 });
  const countDoctors = useCharacters({ is_active: true, category: 'doctors', limit: 1 });
  const countCelebrities = useCharacters({ is_active: true, category: 'celebrities', limit: 1 });
  const countHosts = useCharacters({ is_active: true, category: 'hosts', limit: 1 });
  const countTestimonials = useCharacters({ is_active: true, category: 'testimonials', limit: 1 });
  const counts: Record<TabKey, number | undefined> = {
    all: countAll.data?.total,
    doctors: countDoctors.data?.total,
    celebrities: countCelebrities.data?.total,
    hosts: countHosts.data?.total,
    testimonials: countTestimonials.data?.total,
  };

  const openCharacter = (character: Character) => {
    setParams({ id: character.id });
  };

  if (openId) {
    return <CharacterDetail characterId={openId} canEdit={canEdit} onClose={() => setParams({})} />;
  }

  const items = list.data?.items ?? [];

  const tabOptions: SegOption<TabKey>[] = TABS.map((item) => ({
    value: item.key,
    label: counts[item.key] === undefined ? item.label : `${item.label} · ${counts[item.key]}`,
  }));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <SearchInput
          className={styles.search}
          value={query}
          onChange={setQuery}
          placeholder="Поиск по имени или языку"
          ariaLabel="Поиск персонажей"
        />

        <Seg value={tab} onChange={setTab} options={tabOptions} ariaLabel="Отбор персонажей" />

        <span className={styles.total}>
          {list.isLoading ? 'считаем…' : `${list.data?.total ?? 0} персонажей`}
        </span>

        <div className={styles.toolbarRight}>
          {!userLoading && !canEdit && (
            <span className={styles.readonlyNote}>
              <Eye size={13} strokeWidth={1.5} />
              роль «наблюдатель»: только просмотр
            </span>
          )}
          {canEdit && (
            <Button variant="primary" onClick={() => setAdding(true)}>
              Добавить персонажа
            </Button>
          )}
        </div>
      </div>

      {list.error && <ErrorBanner error={list.error} onRetry={() => void list.refetch()} />}

      {list.isLoading && (
        <div className={styles.grid}>
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div key={index} className={styles.skeletonCard} aria-hidden="true">
              <div className={styles.skeletonFrame} />
              <div className={styles.skeletonBody}>
                <Skeleton width="70%" height={11} />
                <Skeleton width="45%" height={11} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!list.isLoading && !list.error && items.length === 0 && (
        <Card>
          <EmptyGrid
            search={search}
            tab={active}
            canEdit={canEdit}
            onClearSearch={() => setQuery('')}
            onAdd={() => setAdding(true)}
          />
        </Card>
      )}

      {items.length > 0 && (
        <div className={styles.grid}>
          {items.map((character) => (
            <CharacterCard key={character.id} character={character} onOpen={openCharacter} />
          ))}
        </div>
      )}

      {list.data && list.data.total > items.length && (
        <p className="muted">
          Показаны первые {items.length} из {list.data.total}. Уточните поиск, чтобы найти нужного.
        </p>
      )}

      {adding && (
        <AddCharacterDialog
          onClose={() => setAdding(false)}
          onCreated={(characterId) => {
            setAdding(false);
            setParams({ id: characterId });
          }}
        />
      )}
    </div>
  );
}

function EmptyGrid({
  search,
  tab,
  canEdit,
  onClearSearch,
  onAdd,
}: {
  search: string;
  tab: TabDef;
  canEdit: boolean;
  onClearSearch: () => void;
  onAdd: () => void;
}) {
  if (search) {
    return (
      <EmptyState
        icon={<UsersRound size={18} strokeWidth={1.5} />}
        title={`По запросу «${search}» никого`}
        description={`Ничего не нашлось во вкладке «${tab.label.toLowerCase()}». Загляните в архив.`}
        action={
          <Button size="sm" onClick={onClearSearch}>
            Очистить поиск
          </Button>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<UsersRound size={18} strokeWidth={1.5} />}
      title="Здесь пока никого"
      description={tab.emptyText}
      action={
        canEdit ? (
          <Button variant="primary" size="sm" onClick={onAdd}>
            Добавить персонажа
          </Button>
        ) : undefined
      }
    />
  );
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
